// per docs/ui-shape-grafana-authoring.md §2/§3 — the authoring step of the
// dashboard form (caiet §6 #7 "configurare și afișare", UC-DASH-11).
//
// The administrator draws the panels in Grafana's own editor and comes back
// here to pick what they just drew, by title and folder. The uid is shown but
// never typed: it is an identifier nobody remembers, and a typo used to save
// cleanly and render an empty frame for every observer the dashboard reached.
import { useState } from 'react';
import { useTranslate } from '@tolgee/react';
import {
  MudButton,
  MudIcon,
  MudInlineMessage,
  MudLink,
  MudInfoBox,
  MudRadio,
  MudRadioGroup,
  MudSearchInput,
  MudTag,
} from '@egov-moldova/mud-react';
import {
  matchesGrafanaDashboard,
  useGrafanaDashboards,
  type GrafanaDashboardRef,
} from '../../hooks/use-grafana-dashboards';
// Grafana's own editor, not the reading frame: `grafanaEditorSrc` is the address without
// `kiosk`, and the disclosure about what that does and does not separate lives with it.
import { grafanaEditorSrc } from '../../features/dashboards/grafana-embed';

// UC-DASH-08: what the administrator drew lives in Grafana's own store, so the
// definition has to be exportable to reach the next environment. The BFF serves
// it as the provisioning file that belongs under deploy/grafana/dashboards/.
export function grafanaDefinitionUrl(uid: string): string {
  return `/api/v1/admin/grafana/dashboards/${encodeURIComponent(uid)}/file`;
}

interface Props {
  value: string;
  onChange: (uid: string, title: string) => void;
  invalid?: string | undefined;
}

export function GrafanaDashboardPicker({ value, onChange, invalid }: Props): JSX.Element {
  const { t } = useTranslate();
  const [query, setQuery] = useState('');
  const catalogue = useGrafanaDashboards();

  const rows = catalogue.data ?? [];
  const selected = rows.find((row) => row.uid === value);
  const visible = rows.filter((row) => matchesGrafanaDashboard(row, query));
  // A stored uid that is no longer in Grafana must be named, not swallowed:
  // otherwise the form reads as "nothing selected" while the observers keep
  // getting an empty frame.
  const orphanUid = value !== '' && catalogue.isSuccess && !selected ? value : '';

  function openGrafana(url: string): void {
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  return (
    <div className="simsm-grafana-picker">
      <div className="simsm-grafana-picker__authoring">
        <MudButton
          shape="circular"
          variant="primary"
          size="md"
          onClick={() => openGrafana(grafanaEditorSrc({ kind: 'new' }))}
        >
          <MudIcon slot="icon-start" name="plus-large" size={16} />
          {t('dashboards_admin.form.source.author')}
        </MudButton>
        <MudButton
          shape="circular"
          variant="neutral"
          size="md"
          disabled={catalogue.isFetching}
          onClick={() => catalogue.refetch()}
        >
          <MudIcon slot="icon-start" name="rotate-arrow" size={16} />
          {t('dashboards_admin.form.source.reload')}
        </MudButton>
        <p className="simsm-dashboards-admin__form-hint">
          {t('dashboards_admin.form.source.author_hint')}
        </p>
      </div>

      {catalogue.isError ? (
        <MudInfoBox emphasis="strong" role="alert" variant="error">
          {t('dashboards_admin.form.source.load_failed')}
        </MudInfoBox>
      ) : null}

      {orphanUid ? (
        <MudInfoBox emphasis="strong" variant="warning">
          {t('dashboards_admin.form.source.orphan', { uid: orphanUid })}
        </MudInfoBox>
      ) : null}

      <MudSearchInput
        shape="rectangular"
        size="md"
        value={query}
        aria-label={t('dashboards_admin.form.source.search_aria')}
        clearLabel={t('common.filter.clear_search')}
        placeholder={t('dashboards_admin.form.source.search_placeholder')}
        loading={catalogue.isFetching}
        onMudInput={(e: CustomEvent<{ value: string }>) => setQuery(e.detail.value)}
        onMudClear={() => setQuery('')}
      />

      {/* The same field error FormField draws: icon-less, because the wireframe
          draws field errors as plain coloured text, and the small rung of
          mud-inline-message is exactly that text. */}
      {invalid ? (
        <MudInlineMessage variant="error" size="small" hideIcon role="alert">
          {invalid}
        </MudInlineMessage>
      ) : null}

      {catalogue.isLoading ? (
        <p className="simsm-dashboards-admin__form-hint">
          {t('dashboards_admin.form.source.loading')}
        </p>
      ) : visible.length === 0 ? (
        <MudInfoBox emphasis="strong" variant="info">
          {rows.length === 0
            ? t('dashboards_admin.form.source.empty')
            : t('dashboards_admin.form.source.empty_filtered')}
        </MudInfoBox>
      ) : (
        <MudRadioGroup
          aria-label={t('dashboards_admin.form.source.group_aria')}
          className="simsm-grafana-picker__list"
          value={value}
          onMudChange={(e: CustomEvent<{ value: string | undefined }>) => {
            const row = rows.find((candidate) => candidate.uid === e.detail.value);
            if (row) onChange(row.uid, row.title);
          }}
        >
          {visible.map((row) => (
            <GrafanaOption key={row.uid} row={row} />
          ))}
        </MudRadioGroup>
      )}

      {selected ? (
        <p className="simsm-grafana-picker__selected">
          {t('dashboards_admin.form.source.selected', {
            title: selected.title,
            folder: selected.folder,
          })}{' '}
          <MudLink
            href={grafanaEditorSrc({ kind: 'dashboard', uid: selected.uid })}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
          >
            {t('dashboards_admin.form.source.open')}
          </MudLink>{' '}
          <MudLink
            href={grafanaDefinitionUrl(selected.uid)}
            download={`${selected.uid}.json`}
            size="sm"
          >
            {t('dashboards_admin.form.source.export_file')}
          </MudLink>
        </p>
      ) : null}
    </div>
  );
}

function GrafanaOption({ row }: { row: GrafanaDashboardRef }): JSX.Element {
  const { t } = useTranslate();
  return (
    <div className="simsm-grafana-picker__option">
      <MudRadio value={row.uid}>
        <span slot="label">{row.title}</span>
        <span slot="supporting-text">
          {t('dashboards_admin.form.source.option_meta', { folder: row.folder, uid: row.uid })}
        </span>
      </MudRadio>
      <span className="simsm-grafana-picker__tags">
        {row.tags.slice(0, 3).map((tag) => (
          <MudTag key={tag} variant="info" size="sm" label={tag} />
        ))}
      </span>
    </div>
  );
}
